import { BrandMasterIdentity } from '@/components/brands/IdentityEditor';
import type { BrandIntelligenceAd } from '@shared/schema';
import { BrandAdGallery } from '@/components/brands/BrandAdGallery';
// Approved master identity and optional observations from reference ads.

import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { BrandIdentityUploadModal } from "./brand-identity-upload-modal";

interface BrandIdentityProps {
  brandId: number;
}

interface BrandIntelligenceRow {
  id: number;
  brandId: number;
  useForCreation: boolean;
  identityAnalyzedAt: string | null;
  identityAnalysisVersion: string | null;
  lockedColors: Array<{ hex: string; role: string; prominence: number }> | null;
  lockedFonts: Array<{ family: string; weight: string; usage: string }> | null;
  compositionTendencies: {
    preferredLayout: string;
    subjectPosition: string;
    textHierarchy: string;
  } | null;
  propConventions: {
    mustInclude: string[];
    mustAvoid: string[];
    commonThemes: string[];
  } | null;
  photographyStyle: string | null;
  headlineVoice: string | null;
  vibeSummary: string | null;
}

export function BrandIdentityTab({ brandId }: BrandIdentityProps) {
  const { toast } = useToast();
  const [row, setRow] = useState<BrandIntelligenceRow | null>(null);
  const [adCount, setAdCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [ads, setAds] = useState<BrandIntelligenceAd[]>([]);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [intelRes, adsRes] = await Promise.all([
        fetch(`/api/admin/brand-intelligence/${brandId}`),
        fetch(`/api/admin/brand-intelligence/${brandId}/ads`),
      ]);
      const intel = intelRes.ok ? await intelRes.json() : null;
      const adsBody = adsRes.ok ? await adsRes.json() : { ads: [] };
      setRow(intel);
      setAdCount(adsBody.ads?.length ?? 0);
      setAds(adsBody.ads ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const saveField = async (field: string, value: unknown) => {
    const res = await fetch(`/api/admin/brand-intelligence/${brandId}/identity-field`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ field, value }),
    });
    if (!res.ok) {
      toast({ title: `Failed to save ${field}`, variant: "destructive" });
      return;
    }
    setRow((r) => (r ? { ...r, [field]: value } as BrandIntelligenceRow : r));
  };

  const runReanalyze = async () => {
    setReanalyzing(true);
    try {
      const res = await fetch(`/api/admin/brand-intelligence/${brandId}/re-analyze`, {
        method: "POST",
      });
      const result = await res.json();
      if (result.status === "analyzed") {
        toast({ title: "Re-analysis complete" });
      } else {
        toast({
          title: `Re-analysis ${result.status}: ${result.reason ?? ""}`,
          variant: "destructive",
        });
      }
      await load();
    } finally {
      setReanalyzing(false);
    }
  };

  const deleteAd = async (adId: number) => {
    const res = await fetch(`/api/admin/brand-intelligence/ads/${adId}`, { method: "DELETE" });
    if (!res.ok) {
      toast({ title: "Failed to delete ad", variant: "destructive" });
      return;
    }
    await load();
  };

  if (loading) return <div className="brand-tab-stack space-y-5"><BrandMasterIdentity brandId={brandId}/><p className="text-sm text-slate-500">Loading observed ad style…</p></div>;
  if (!row) return <div className="brand-tab-stack space-y-5"><BrandMasterIdentity brandId={brandId}/><p className="text-sm text-slate-500">No observed ad style yet. Add reference ads to analyze their style.</p><BrandAdGallery ads={ads} onDelete={deleteAd}/><button className="self-start rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium hover:bg-slate-50" onClick={()=>setShowUpload(true)}>Upload reference ads</button>{showUpload&&<BrandIdentityUploadModal brandId={brandId} onClose={()=>setShowUpload(false)} onUploaded={load}/>}</div>;

  const analyzedLabel = row.identityAnalyzedAt
    ? new Date(row.identityAnalyzedAt).toLocaleString()
    : "Not yet analyzed";

  return (
    <div className="brand-tab-stack space-y-5">
      <BrandMasterIdentity brandId={brandId}/>
      <div data-testid="observed-ad-style" className="space-y-5">
      <div className="text-xs text-slate-500">
        Status: {row.identityAnalyzedAt ? "✓ Analyzed " : ""}{analyzedLabel}
      </div>

      <details className="border-t border-slate-100 pt-3">
        <summary className="cursor-pointer text-sm font-medium">See full details</summary>
        <div className="mt-4 space-y-5">
          <p className="text-xs text-slate-600">
            Historical ad observations are available here for reference. New ads use your approved identity and the selected winning ad’s composition and visual style.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              {adCount < 5 && <span>Add at least 5 reference ads to analyze their style. </span>}
              {adCount} ads · v{row.identityAnalysisVersion ?? "—"}
            </p>
            <button onClick={runReanalyze} disabled={reanalyzing || adCount < 5}
              className="text-xs px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {reanalyzing ? "Analyzing…" : "Analyze observed ad style"}
            </button>
          </div>

      {/* ── PHOTOGRAPHY STYLE / HEADLINE VOICE / VIBE ─────── */}
      {[
        { key: "photographyStyle", label: "Photography Style" },
        { key: "headlineVoice", label: "Headline Voice" },
        { key: "vibeSummary", label: "Vibe Summary" },
      ].map(({ key, label }) => (
        <section key={key} className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">{label}</h3>
          <textarea
            defaultValue={(row as any)[key] ?? ""}
            onBlur={(e) => saveField(key, e.target.value)}
            rows={3}
            className="w-full bg-white border border-slate-300 rounded p-2 text-xs text-slate-900 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </section>
      ))}

      {/* ── COMPOSITION TENDENCIES ────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Composition Tendencies</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-600">
            Preferred Layout
            <select
              defaultValue={row.compositionTendencies?.preferredLayout ?? "centered_hero"}
              onChange={(e) => saveField("compositionTendencies", { ...(row.compositionTendencies ?? {} as any), preferredLayout: e.target.value })}
              className="block w-full mt-1 bg-white border border-slate-300 rounded px-2 py-1 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {["centered_hero", "rule_of_thirds", "flatlay", "comparison_chart", "split_screen", "other"].map((l) => <option key={l}>{l}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            Subject Position
            <select
              defaultValue={row.compositionTendencies?.subjectPosition ?? "center"}
              onChange={(e) => saveField("compositionTendencies", { ...(row.compositionTendencies ?? {} as any), subjectPosition: e.target.value })}
              className="block w-full mt-1 bg-white border border-slate-300 rounded px-2 py-1 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {["center", "left", "right"].map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
        </div>
        <label className="text-xs text-slate-600 block mt-3">
          Text Hierarchy
          <input
            defaultValue={row.compositionTendencies?.textHierarchy ?? ""}
            onBlur={(e) => saveField("compositionTendencies", { ...(row.compositionTendencies ?? {} as any), textHierarchy: e.target.value })}
            className="block w-full mt-1 bg-white border border-slate-300 rounded px-2 py-1 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </label>
      </section>

      {/* ── PROP CONVENTIONS ──────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Prop Conventions</h3>
        {(["mustInclude", "mustAvoid", "commonThemes"] as const).map((key) => (
          <div key={key} className="mb-3 last:mb-0">
            <p className="text-xs text-slate-600 mb-1">{key.replace(/([A-Z])/g, " $1").toLowerCase()}</p>
            <div className="flex flex-wrap gap-1">
              {((row.propConventions?.[key] ?? []) as string[]).map((tag, i) => (
                <span key={i} className="bg-slate-100 border border-slate-200 text-xs text-slate-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  {tag}
                  <button onClick={() => {
                    const list = [...((row.propConventions?.[key] ?? []) as string[])];
                    list.splice(i, 1);
                    saveField("propConventions", { ...(row.propConventions ?? {mustInclude: [], mustAvoid: [], commonThemes: []}), [key]: list });
                  }} className="text-red-500 ml-1 hover:text-red-600">×</button>
                </span>
              ))}
              <input
                placeholder="+ add"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = (e.target as HTMLInputElement).value.trim();
                    if (!v) return;
                    const list = [...((row.propConventions?.[key] ?? []) as string[]), v];
                    saveField("propConventions", { ...(row.propConventions ?? {mustInclude: [], mustAvoid: [], commonThemes: []}), [key]: list });
                    (e.target as HTMLInputElement).value = "";
                  }
                }}
                className="bg-white border border-slate-300 rounded px-2 py-0.5 text-xs text-slate-900 w-24 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        ))}
      </section>

      {/* ── SOURCE ADS ─────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Source Ads</h3>
          <button
            onClick={() => setShowUpload(true)}
            className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
          >Upload Media +</button>
        </div>
        <BrandAdGallery ads={ads} onDelete={deleteAd} />
      </section>

        </div>
      </details>
      </div>

      {showUpload && (
        <BrandIdentityUploadModal
          brandId={brandId}
          onClose={() => setShowUpload(false)}
          onUploaded={load}
        />
      )}
    </div>
  );
}
